import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { CompanyType, CreateBrandFormType, PolygonType } from 'utils/types';
import { days } from 'utils/constants';
import { NewBrandContext } from './context';
import { MetadataItemType } from 'pages/metadata/types';
import { getWorkingHoursComponentFormatFromApi, handleApiError } from 'utils/functions';
import { ENUM_BRAND_TYPE } from 'utils/enum';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';

const NewBrandProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const daysArray = getWorkingHoursComponentFormatFromApi(days());

  const [createBrandForm, setCreateBrandForm] = useState<CreateBrandFormType>({
    brand_keywords: [] as MetadataItemType[],
    brand_business_metadatas: [] as MetadataItemType[],
    brand_descriptive_metadatas: [] as MetadataItemType[],
    brand_working_hours: daysArray,
    name: '',
    description: '',
    slug: '',
    summary: '',
    pop_up: false,
    images: [],
    new: false,
    top: false,
    popup_color: '',
    coming_soon: false,
    coming_soon_text: '',
    short_text: '',
    logo: '',
    brand_type: ENUM_BRAND_TYPE.FOODCOURT,
    cutoff_time: null,
    preorder: false,
    all_company_available: true,
    company_ids: [],
    polygon: []
  });
  const [disabled, setDisabled] = useState<boolean>(false);
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [loadingCompanyOptions, setLoadingCompanyOptions] = useState(false);
  const [loadingPolygons, setLoadingPolygons] = useState(false);
  const [availablePolygons, setAvailablePolygons] = useState<PolygonType[]>([]);

  const { activeCokitchen } = useDashboardContext();
  const companyOptions = useMemo(() => {
    return companies.map((c) => ({ value: c.id, text: c.name }));
  }, [companies]);

  const loadCompanies = async () => {
    try {
      setLoadingCompanyOptions(true);
      const params = { status: 'verified', page: 1, size: Number.MAX_SAFE_INTEGER };
      const res = await apiService.get('company/all', {
        params,
        baseURL: process.env.REACT_APP_FCB_API_URL
      });
      setCompanies(res.data.data.results);
    } catch (err) {
      setCompanies([]);
    } finally {
      setLoadingCompanyOptions(false);
    }
  };

  const fetchPolygons = async () => {
    if (!activeCokitchen.id) return;
    try {
      setLoadingPolygons(true);
      const res = await apiService.get(`internal/cokitchen/polygons/${activeCokitchen.id}`, {
        isInternal: true
      });

      const polygons: PolygonType[] = res.data.data || [];
      polygons.map((p) => ({
        ...p,
        available: true
      }));
      setAvailablePolygons(polygons);
      setCreateBrandForm({
        ...createBrandForm,
        polygon: polygons.map((polygon) => ({ ...polygon, available: true }))
      });
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoadingPolygons(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    fetchPolygons();
  }, []);

  return (
    <NewBrandContext.Provider
      value={{
        createBrandForm,
        setCreateBrandForm,
        disabled,
        setDisabled,
        companyOptions,
        loadingCompanyOptions,
        availablePolygons,
        loadingPolygons
      }}>
      {children}
    </NewBrandContext.Provider>
  );
};

export default NewBrandProvider;
